import React, { useEffect, useRef, useState } from "react";
import { PlusLink } from "@clarkinc/zest-design-system";
import { cls } from "@wss/cls";
import { User } from "~/common/Store/models/User";
import {
  SelectedProduct,
  PromotedProductBrandAd,
} from "~/common/Store/models/ProductListing";
import Pricing from "../../Pricing";
import Stars from "~/common/Stars";
import spritePath from "~/common/Icon/critical-symbol.svg";
import Button from "~/common/Buttons";
import trackBrandAdService, {
  Request,
} from "~/productListing/trackBrandAdService";
import { addPromotedProductAdToSessionStorage } from "~/productListing/addAdsToSessionStorage";
import ProductListingVideo from "~/productListing/ProductListingVideo";
import PromotedTag from "~/common/PromotedTag/PromotedTag";

interface Props {
  brandAd: PromotedProductBrandAd;
  user: User;
  mounted: boolean;
  selectedProduct: SelectedProduct;
  isTabletView?: boolean;
}

const PromoAdMedia = ({
  brandAd,
  mounted,
  intersectionObserved,
}: {
  brandAd: PromotedProductBrandAd;
  mounted: boolean;
  intersectionObserved: boolean;
}) => {
  const promoAdContentColumnClassNames = "grow basis-0 w-full min-w-0 max-w-md";

  return (
    <div
      data-testid="promo-ad-media"
      className={cls(
        "self-center",
        "md:self-start",
        promoAdContentColumnClassNames
      )}
    >
      {brandAd.showVideoIfAvailable && !!brandAd.video && (
        <ProductListingVideo
          video={brandAd.video}
          autoPlay
          muted
          mounted={mounted}
          shouldLoopVideo
          playVideo={intersectionObserved}
        />
      )}
      {!brandAd.showVideoIfAvailable &&
        !!brandAd.product.primaryImagePath &&
        !brandAd.imageUrlOverride && (
          <img
            src={brandAd.product.primaryImagePath.replace(
              "/medium/",
              "/large/"
            )}
            alt=""
            className={cls(
              "block",
              "min-h-[210px]",
              "mx-auto",
              "w-full",
              "xsl:w-[300px]"
            )}
            data-testid="promo-image"
          />
        )}
      {!brandAd.showVideoIfAvailable && brandAd.imageUrlOverride && (
        <img
          src={brandAd.imageUrlOverride}
          alt=""
          className={cls(
            "block",
            "min-h-[210px]",
            "mx-auto",
            "w-full",
            "xsl:w-[300px]"
          )}
          data-testid="promo-image"
        />
      )}
    </div>
  );
};

const ProductAd = ({
  brandAd,
  user,
  mounted,
  selectedProduct,
  isTabletView = false,
}: Props) => {
  const { product } = brandAd;
  const [intersectionObserved, setIntersectionObserved] = useState(false);

  const observerRef = useRef<IntersectionObserver | null>(null);
  const adRef = useRef<HTMLDivElement>(null);
  const requestRef = useRef<Request>({
    adId: -1,
    userId: null,
    sessionId: undefined,
    promotedBrandAdType: -1,
    url: undefined,
  });
  const showPlusLink =
    product.isWebstaurantPlusEligible &&
    !product.isRequestAQuoteEnabled &&
    !product.price.minimumAdvertisedPriceOverride.priceLevel5;

  useEffect(() => {
    return () => {
      if (observerRef.current !== null) {
        observerRef.current.disconnect();
      }
    };
  }, []);

  useEffect(() => {
    requestRef.current.sessionId = user.cfid;
    requestRef.current.userId = Number(user.index) || null;
    requestRef.current.adId = brandAd.adId;
    requestRef.current.promotedBrandAdType = brandAd.promotedBrandAdType;
    requestRef.current.url = brandAd.product.link;

    if (adRef.current !== null && observerRef.current === null) {
      const observerCallback = (
        observations: IntersectionObserverEntry[],
        observer: IntersectionObserver
      ) => {
        const observation = observations[0];
        if (
          observation !== undefined &&
          observation.isIntersecting &&
          observation.intersectionRatio >= 0.8
        ) {
          trackBrandAdService("impression", requestRef.current);
          setIntersectionObserved(true);
          observer.disconnect();
        }
      };

      const observerOptions: IntersectionObserverInit = {
        root: null,
        rootMargin: "0px",
        threshold: 0.8,
      };

      observerRef.current = new IntersectionObserver(
        observerCallback,
        observerOptions
      );

      observerRef.current.observe(adRef.current);
    }
  }, [
    brandAd.adId,
    brandAd.promotedBrandAdType,
    user.index,
    user.cfid,
    brandAd.product.link,
  ]);

  const onAdClick = async (e: React.MouseEvent<HTMLAnchorElement>) => {
    e.preventDefault();
    await trackBrandAdService("click", requestRef.current);
    addPromotedProductAdToSessionStorage(
      brandAd.promotedBrandAdType === 0
        ? "CategoryListingPromotedProduct"
        : "SearchListingPromotedProduct",
      brandAd.adId,
      brandAd.product.itemNumber
    );

    window.location.href = brandAd.product.link || "";
  };

  const productAdWrapperClassNames = cls(
    "bg-white",
    "border",
    "border-gray-200",
    "border-l-0",
    "border-solid",
    "border-t-0",
    "box-border",
    "col-span-full",
    "relative",
    "row-start-2"
  );

  const promoAdContentClassNames = cls(
    "flex",
    "items-center",
    "justify-between",
    "w-full",
    "lt:gap-6"
  );

  const promoAdContainerClassNames = cls(
    "flex",
    "flex-col",
    "gap-y-4",
    "items-center",
    "justify-center",
    "px-6",
    "py-8",
    "md:flex-row",
    "md:gap-x-4",
    "md:px-6",
    "md:py-12"
  );

  const promoAdContentColumnClassNames = "grow basis-0 w-full min-w-0 max-w-md";

  return (
    <section className={productAdWrapperClassNames}>
      <div
        data-testid="promoted-product-container"
        className={promoAdContainerClassNames}
      >
        <p
          data-testid="promoted-tag"
          className="absolute mt-0.5 top-1 right-0 pr-2 inline-flex items-center"
        >
          <PromotedTag />
        </p>
        {isTabletView && (
          <PromoAdMedia
            brandAd={brandAd}
            mounted={mounted}
            intersectionObserved={intersectionObserved}
          />
        )}
        <div
          className={cls(
            "flex",
            "gap-4",
            "items-center",
            "justify-center",
            "max-w-md",
            "w-full",
            "md:items-start",
            "md:mb-auto",
            "md:px-3",
            "md:pb-3",
            "md:pt-0",
            "md:w-1/2",
            "xl:my-0",
            "xxxl:max-w-2xl"
          )}
        >
          {brandAd.showVideoIfAvailable && (
            <img
              src={brandAd.imageUrlOverride || product.primaryImagePath}
              alt=""
              className={cls(
                "block",
                "border",
                "border-gray-200",
                "border-solid",
                "h-[111px]",
                "rounded",
                "self-start",
                "w-[111px]",
                "xl:h-[204px]",
                "xl:w-[204px]"
              )}
              data-testid="promo-image"
            />
          )}
          <div
            ref={adRef}
            data-testid="promo-ad-content"
            className={promoAdContentClassNames}
          >
            <div
              data-testid="promo-ad-details"
              className={cls(
                "promo-ad-details",
                promoAdContentColumnClassNames
              )}
            >
              {!!product.brandLogo && !!product.brandLink && (
                <a href={product.brandLink}>
                  <img
                    width="64px"
                    alt={product.brand}
                    src={`/images/vendor/medium/${product.brandLogo}`}
                    className="pb-3"
                  />
                </a>
              )}
              <p
                className={cls(
                  "font-semibold",
                  "leading-tight",
                  "m-0",
                  "text-green-500",
                  "text-lg",
                  "leading-[22.5px]"
                )}
              >
                <a href={product.link} onClick={onAdClick}>
                  {product.description}
                </a>
              </p>
              <div className="flex gap-2 items-center h-6">
                {(showPlusLink || product.overallRating > 3) && (
                  <div
                    className={`${
                      !product.isWebstaurantPlusEligible &&
                      product.oneToFiveStarRating > 3
                        ? "-mb-1"
                        : ""
                    }`}
                  >
                    <div className="flex items-center gap-2">
                      {showPlusLink && (
                        <PlusLink
                          iconProps={{
                            spriteSheetHref: spritePath,
                            // eslint-disable-next-line @typescript-eslint/ban-ts-comment
                            // @ts-ignore
                            "data-testid": "itemPlus",
                          }}
                          linkProps={{
                            className: cls("inline-flex", "align-middle"),
                            rel: "",
                          }}
                        />
                      )}
                      {product.overallRating > 3 && (
                        <Stars rating={product.overallRating} />
                      )}
                    </div>
                  </div>
                )}
                <p
                  className={cls(
                    "font-normal",
                    "my-0",
                    "text-gray-700",
                    "text-xs"
                  )}
                >
                  {`#${product.itemNumber.toUpperCase()}`}
                </p>
              </div>
              <div
                className={cls(
                  "flex",
                  "flex-col",
                  "items-start",
                  "py-3",
                  "text-left",
                  "w-1/2"
                )}
              >
                <Pricing
                  product={product}
                  mounted={mounted}
                  user={user}
                  selectedProduct={selectedProduct}
                  hideQuantityDiscountTable
                  customizablePricingExtraClassOne={cls(
                    "font-semibold",
                    "leading-[30px]",
                    "mb-1",
                    "relative",
                    "text-2xl",
                    "text-red-500",
                    "z-5"
                  )}
                  customizablePricingExtraClassTwo={cls(
                    "inline-block",
                    "text-gray-800",
                    "text-xs",
                    "whitespace-nowrap"
                  )}
                  isBrandAd
                />
              </div>
              <Button
                href={product.link}
                onClick={onAdClick}
                color="red"
                size="custom"
                className={cls(
                  "btn",
                  "btn-block",
                  "btn-cart",
                  "md:w-40",
                  "w-full"
                )}
              >
                Shop Product
              </Button>
            </div>
          </div>
        </div>
        {!isTabletView && (
          <PromoAdMedia
            brandAd={brandAd}
            mounted={mounted}
            intersectionObserved={intersectionObserved}
          />
        )}
      </div>
    </section>
  );
};

export default ProductAd;
